import { THEME } from '@Theme';
import { CSSProperties, StyleSheet } from 'aphrodite';
import { brandPrimary, brandPrimaryText, header, navigation, titles, white } from '../../../styles/colors';
import { BrowserMediaQueries, CssSelectors, truncateTextStyle } from '../../../styles/styles';

const {
	colors,
	common,
	spacing: { sm, md, lg },
	queries,
} = THEME;

const updateRecipientsHeader: CSSProperties = {
	color: colors.text.dark,
	fontSize: 18,
	marginTop: 8,
	textTransform: 'none',
};

export const styleSheet = StyleSheet.create({
	buttonContainer: {
		display: 'flex',
		gap: 8,
		padding: `12px 16px`,
	},
	alert: {
		position: 'absolute',
		top: '100%',
	},
	addPhoneNotificationContent: {
		alignItems: 'center',
		color: titles,
		display: 'flex',
		...CssSelectors.allChildren(
			{
				marginRight: 10,
			},
			'svg'
		),
	},
	anchor: {
		marginLeft: 10,
		marginRight: 10,
	},
	backChevron: {
		marginRight: 5,
		transform: 'rotateZ(90deg)',
	},
	button: {
		alignItems: 'center',
		color: brandPrimaryText,
		display: 'flex',
	},
	column: {
		padding: `${lg}px ${md}px`,
		width: 320,
	},
	contactColHeaderLinks: {
		display: 'flex',
		justifyContent: 'space-between',
		paddingRight: 5,
	},
	contactInfoContainer: {
		position: 'relative',
		...CssSelectors.allChildren(
			{
				'::-webkit-scrollbar': {
					background: colors.background.main,
					width: 8,
				},
				'::-webkit-scrollbar-thumb': {
					backgroundColor: '#D8D8D8',
					borderRadius: 10,
					padding: '0 2px',
				},
				boxSizing: 'border-box',
				maxHeight: '100%',
				maxWidth: '100%',
				minHeight: '100%',
				overflowX: 'hidden',
				overflowY: 'auto',
				padding: '40px 8px 16px 4px',
				...CssSelectors.allChildren(
					{
						maxHeight: '100%',
						maxWidth: '100%',
						minHeight: 200,
						overflow: 'unset',
					},
					'.editable-contact-inner'
				),
			},
			'.editable-contact'
		),
	},
	container: {
		alignItems: 'stretch',
		display: 'flex',
		height: '100%',
	},
	conversationContainer: {
		background: colors.background.main,
		borderLeft: common.border,
		display: 'flex',
		flex: '1 0 33%',
		flexDirection: 'column',
		minWidth: 310,
		...CssSelectors.allChildren(
			{
				display: 'flex',
				flexDirection: 'column-reverse',
				flexGrow: 1,
				padding: '10px 20px',
				[BrowserMediaQueries.IE11]: {
					'-ms-overflow-style': 'none',
					flexDirection: 'column',
					transform: 'rotate(180deg)',
				},
			},
			'.conversation'
		),
	},
	conversationHeader: {
		alignItems: 'center',
		background: '#fff',
		borderBottom: common.border,
		borderRight: common.border,
		borderTop: common.border,
		borderRadius: `0 ${common.borderRadius}px 0 0`,
		boxSizing: 'border-box',
		display: 'flex',
		minWidth: '100%',
		minHeight: 72,
		position: 'relative',
		flexShrink: 0,
		paddingLeft: 8,
		paddingRight: 8,
	},
	conversationHeaderContentContainer: {
		borderRadius: `0 ${common.borderRadius}px 0 0`,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		minHeight: 44,
		width: '100%',
		...CssSelectors.allChildren({
			margin: 0,
			textAlign: 'center',
		}),
	},
	conversationHeaderLeft: {
		borderRadius: `0 ${common.borderRadius}px 0 0`,
		justifyContent: 'flex-start',
		...CssSelectors.allDescendants(
			{
				minHeight: 42,
				marginRight: 10,
				marginTop: 3,
				alignContent: 'center',
			},
			'svg.svg-icon'
		),
	},
	conversationHeaderNames: {
		...truncateTextStyle,
		fontSize: 18,
		maxWidth: '95%',
	},
	conversationHeaderNumbers: {
		...truncateTextStyle,
		color: header,
		maxWidth: '95%',
	},
	conversationItem: {
		':not(:first-child)': {
			marginTop: 2,
		},
		[queries.lg]: {
			justifyContent: 'center',
		},
	},
	conversationsCol: {
		borderLeft: common.border,
		borderBottom: common.border,
		borderRadius: `${common.borderRadius}px 0 0 ${common.borderRadius}px`,
		[queries.lg]: {
			maxWidth: 160,
			minWidth: 160,
		},
		padding: 0,
		display: 'flex',
		flexDirection: 'column',
	},
	conversationsContainer: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		overflowX: 'hidden',
		overflowY: 'auto',
	},
	conversationsHeader: {
		[queries.lg]: {
			flexDirection: 'column',
		},
		borderTop: common.border,
		borderRadius: `${common.borderRadius}px 0 0 0`,
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${md}px ${lg}px`,
		...CssSelectors.allDescendants(
			{
				minWidth: 40,
				maxHeight: 44,
			},
			'.new-conversation-cta'
		),
	},
	conversationsLoading: {
		marginTop: 75,
	},
	conversationsSearchContainer: {
		padding: '20px 0',
	},
	forwardChevron: {
		marginLeft: 5,
		transform: 'rotateZ(-90deg)',
	},
	inputContainer: {
		background: colors.background.main,
		...CssSelectors.allChildren(
			{
				fontSize: '16px !important',
			},
			'input'
		),
	},
	noConversations: {
		color: navigation,
		marginTop: 40,
		textAlign: 'center',
	},
	noPhoneContactSearchAnchor: {
		width: '100%',
		...CssSelectors.allDescendants(
			{
				width: '100%',
			},
			'.autocomplete-search-field-input'
		),
	},
	quickAddEntity: {
		borderTop: common.border,
		marginTop: 25,
		...CssSelectors.allDescendants(
			{
				...updateRecipientsHeader,
			},
			'.quick-add-entity-title'
		),
	},
	quickAddEntityFields: {
		marginRight: 0,
	},
	recipientSearch: {
		maxWidth: 800,
	},
	searchInputWrapper: {
		width: '100%',
		backgroundColor: white,
		borderRadius: 8,
		padding: 8,
		':focus-within': {
			borderColor: brandPrimary,
		},
	},
	searchIcon: {
		marginRight: 8,
	},
	textContainer: {
		color: header,
		display: 'flex',
		alignItems: 'center',
		...CssSelectors.allChildren(
			{
				marginRight: 2,
				marginTop: 2,
			},
			'svg'
		),
	},
	textContainerLeftAlign: {
		justifyContent: 'flex-start',
	},
	unreadOnlyCheckbox: {
		margin: `${md}px ${lg}px`,
		[queries.lg]: {
			margin: `${sm}px 0 ${sm}px ${md}px`,
		},
	},
	updateRecipientsHeader: {
		...updateRecipientsHeader,
	},
	newTextMessageButton: {
		':has(*) > svg': {
			width: 20,
			height: 20,
		},
	},
	yourNumber: {
		[queries.lg]: {
			marginBottom: 24,
		},
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		...CssSelectors.allChildren(
			{
				fontSize: 12,
				textTransform: 'uppercase',
			},
			':first-child'
		),
		...CssSelectors.allChildren(
			{
				fontSize: 16,
			},
			':last-child'
		),
	},
});
